/**
 * nativeTxConfirm.ts
 *
 * Formats parsed dApp transaction actions into a plain-text message for the
 * NATIVE confirmation dialog (see nativeDialog.ts).
 *
 * NOTE: this used to inject a styled HTML overlay into the dApp WebView via
 * InAppBrowser.executeScript. That was insecure — the confirmation UI lived in
 * the dApp-controlled page, so a malicious dApp could synthesize a click on the
 * "Approve" button (or intercept its handler) and auto-approve a transfer with
 * no user interaction. The confirmation now renders as a native OS dialog and
 * this module only produces the human-readable text shown inside it.
 */

import type { ParsedTxAction } from './txActions'
import { actionDangerLabel, serviceActionNote, summarizeActionData } from './txActions'

/** Build the message body shown in the native transaction confirmation dialog. */
export function buildNativeTxConfirmMessage(
  actions: ParsedTxAction[],
  opts?: { broadcast?: boolean },
): string {
  const lines: string[] = []

  if (actions.length === 0) {
    lines.push('⚠ This dApp requested a signature but no readable actions were found.')
    lines.push('Only approve if you fully trust this dApp.')
    return lines.join('\n')
  }

  actions.forEach((a, i) => {
    const note = serviceActionNote(a)
    if (note) {
      lines.push(`${i + 1}. ${note}`)
      return
    }
    const badge = actionDangerLabel(a.danger)
    const header = `${i + 1}. ${a.account} → ${a.name}${badge ? `  [${badge}]` : ''}`
    lines.push(header)
    const summary =
      a.danger === 'transfer' && a.data.from && a.data.to && a.data.quantity
        ? `${String(a.data.quantity)}: ${String(a.data.from)} → ${String(a.data.to)}` +
          (a.data.memo ? `\n   memo: ${String(a.data.memo)}` : '')
        : Object.keys(a.data).length > 0
          ? summarizeActionData(a.data)
          : a.rawHex
            ? '(details could not be decoded)'
            : ''
    if (summary) lines.push(`   ${summary}`)
  })

  lines.push('')
  lines.push(
    opts?.broadcast === false
      ? 'You are about to SIGN this transaction.'
      : 'You are about to SIGN and BROADCAST this transaction.',
  )

  return lines.join('\n')
}
